/**
 * Modale "Vue client" — détail complet.
 *
 * Layout fidèle au mockup :
 *  - Header : avatar + nom + statut + score santé donut
 *  - Tabs internes : Vue d'ensemble / Activité / Utilisation produit / Documents / Notes
 *  - Mini-stats grid : Dernière activité, Utilisateurs, Features utilisées, Commandes, Dépenses totales
 *  - Cartes : Informations générales + Contacts
 *  - Actions footer : Relancer / Créer tâche / Ajouter note / Fermer
 */
import React from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import {
  X, Phone, MapPin, Clock, Users, Package, ShoppingBag, Wallet,
  Send, ClipboardList, StickyNote, Activity, FileText, Loader2,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import {
  initials, initialsBg, RISK_BADGE, RISK_LABEL, fmtFcfaFull, fmtDateFr,
} from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Donut({ value = 0, size = 72, stroke = 8, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(100, value)) / 100);
  const color = value >= 70 ? '#10b981' : value >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%" y="48%" textAnchor="middle" dominantBaseline="middle"
          className="fill-gray-900"
          style={{ fontSize: size * 0.28, fontWeight: 700 }}
        >
          {value}
        </text>
        <text
          x="50%" y="72%" textAnchor="middle" dominantBaseline="middle"
          className="fill-gray-400"
          style={{ fontSize: size * 0.13 }}
        >
          /100
        </text>
      </svg>
      {label && <p className="text-[10px] text-emerald-600 font-medium mt-1">{label}</p>}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub, color = 'text-gray-700' }) {
  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <p className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</p>
      </div>
      <p className="text-base font-semibold text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ClientDetailModal() {
  const { modal, client, close, switchModal } = useClientActions();
  const isOpen = modal === 'view' && !!client;

  const [details, setDetails] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [actions, setActions] = React.useState([]);

  React.useEffect(() => {
    if (!isOpen || !client?.companyName) return;
    let cancel = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/client-engagement/client/${encodeURIComponent(client.companyName)}/details?days=90`),
      axios.get(`${API}/admin/customer-success/actions/${encodeURIComponent(client.companyName)}?limit=100`).catch(() => ({ data: { actions: [] } })),
    ])
      .then(([d, a]) => {
        if (cancel) return;
        setDetails(d.data);
        setActions(a.data?.actions || []);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('client details fetch error', e);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [isOpen, client?.companyName]);

  if (!isOpen) return null;

  const merged = {
    ...client,
    ...(details || {}),
    metrics: details?.metrics || {},
    score: details?.score || { value: client.score || 0, label: '—', risk: 'a_surveiller' },
    contacts: details?.contacts || {},
    profile: details?.profile || {},
  };

  const score = merged.score?.value ?? 0;
  const scoreLabel = merged.score?.label || '—';
  const riskKey = merged.score?.risk || 'a_surveiller';
  const m = merged.metrics || {};

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="client-detail-modal"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className={`w-14 h-14 rounded-full ${initialsBg(client.companyName)} text-white flex items-center justify-center text-lg font-semibold shrink-0`}>
              {initials(client.companyName)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-semibold text-gray-900 truncate">{client.companyName}</h2>
                <Badge className={`${RISK_BADGE[riskKey]} border text-[10px]`}>
                  {RISK_LABEL[riskKey]}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                {(merged.contacts?.primary?.email || client.primaryEmail) && <span>{merged.contacts?.primary?.email || client.primaryEmail}</span>}
                {(merged.contacts?.primary?.phone || client.primaryPhone) && <><span>•</span><span className="flex items-center gap-1"><Phone className="w-3 h-3" />{merged.contacts?.primary?.phone || client.primaryPhone}</span></>}
                <span>•</span><span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Abidjan, Côte d'Ivoire</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Donut value={score} label={scoreLabel} />
            <button onClick={close} className="text-gray-400 hover:text-gray-600 self-start" data-testid="close-view-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="px-6 pt-3 bg-transparent border-b border-gray-100 rounded-none justify-start gap-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activité</TabsTrigger>
            <TabsTrigger value="product" data-testid="tab-product">Utilisation produit</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="overflow-auto px-6 pb-6 pt-4 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-4 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement des détails...
              </div>
            )}
            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <MiniStat
                icon={Clock} label="Dernière activité"
                value={m.lastSeen ? fmtDateFr(m.lastSeen) : 'Aucune'}
                sub={m.daysSinceLast === 0 ? "Aujourd'hui" : m.daysSinceLast != null ? `Il y a ${m.daysSinceLast} j` : 'Sur 90j'}
                color="text-amber-600"
              />
              <MiniStat
                icon={Users} label="Utilisateurs"
                value={merged.profile?.userCount ?? m.uniqueUsers ?? 1}
                sub={`${m.uniqueUsers ?? 0} actif(s)`}
                color="text-violet-600"
              />
              <MiniStat
                icon={Package} label="Fonctions utilisées"
                value={`${m.featuresUsedCount ?? 0}`}
                sub="dernier trimestre"
                color="text-emerald-600"
              />
              <MiniStat
                icon={ShoppingBag} label="Commandes"
                value={m.ordersCount ?? 0}
                sub="au total" color="text-blue-600"
              />
              <MiniStat
                icon={Wallet} label="Dépenses totales"
                value={fmtFcfaFull(m.totalSpend ?? 0)}
                sub={`Panier moyen: ${fmtFcfaFull(m.avgOrderValue ?? 0)}`}
                color="text-emerald-600"
              />
            </div>

            {/* Informations générales + Contacts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-100 rounded-lg p-4 bg-white">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Informations générales</h3>
                <dl className="space-y-2 text-xs">
                  <Row label="Date d'inscription" value={fmtDateFr(merged.profile?.createdAt) || '—'} />
                  <Row label="Type de compte" value={merged.profile?.accountType || 'Entreprise'} />
                  <Row label="Secteur d'activité" value={merged.profile?.sector || '—'} />
                  <Row label="Taille de l'entreprise" value={merged.profile?.companySize || '—'} />
                  <Row label="Compte actif" value={merged.profile?.isActive ? 'Oui' : 'Non'} />
                </dl>
              </div>
              <div className="border border-gray-100 rounded-lg p-4 bg-white">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Contacts</h3>
                <div className="space-y-3 text-xs">
                  {merged.contacts?.primary ? (
                    <ContactBlock
                      role="Contact principal"
                      name={merged.contacts.primary.name || '—'}
                      email={merged.contacts.primary.email || '—'}
                      phone={merged.contacts.primary.phone || '—'}
                    />
                  ) : <p className="text-gray-400">Aucun contact identifié.</p>}
                  {merged.contacts?.secondary && (
                    <ContactBlock
                      role="Contact opérationnel"
                      name={merged.contacts.secondary.name || '—'}
                      email={merged.contacts.secondary.email || '—'}
                      phone={merged.contacts.secondary.phone || '—'}
                    />
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="overflow-auto px-6 pb-6 pt-4">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto my-8" />
            ) : (details?.activity?.length ?? 0) === 0 ? (
              <EmptyTab icon={Activity} title="Aucune activité récente" hint="Ce client n'a pas généré d'événements sur les 90 derniers jours." />
            ) : (
              <ul className="space-y-2">
                {details.activity.map((ev, i) => (
                  <li key={i} className="flex items-start gap-3 p-2 border border-gray-100 rounded-md hover:bg-gray-50" data-testid={`detail-activity-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                      {ev.eventType?.slice(0, 2).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900">{ev.eventType || '—'}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {ev.metadata?.path || ev.metadata?.name || ev.userEmail || '—'}
                      </p>
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">{fmtDateFr(ev.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="product" className="overflow-auto px-6 pb-6 pt-4">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto my-8" />
            ) : (details?.productUsage?.length ?? 0) === 0 ? (
              <EmptyTab icon={Package} title="Aucune fonctionnalité utilisée" hint="Ce client n'a pas activé de feature trackée sur la période." />
            ) : (
              <div className="space-y-2">
                {details.productUsage.map((f, i) => (
                  <div key={i} className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2" data-testid={`detail-feature-${i}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900">{f.label}</p>
                      <code className="text-[10px] text-gray-400">{f.feature}</code>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">{f.uses}</p>
                      <p className="text-[10px] text-gray-400">{fmtDateFr(f.lastUsed)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="documents" className="overflow-auto px-6 pb-6 pt-4">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto my-8" />
            ) : (details?.documents?.length ?? 0) === 0 ? (
              <EmptyTab icon={FileText} title="Aucun document" hint="Aucune facture ou bon signé ne correspond à ce client." />
            ) : (
              <ul className="space-y-2">
                {details.documents.map((d, i) => (
                  <li key={i} className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2" data-testid={`detail-doc-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className={`w-4 h-4 ${d.kind === 'invoice' ? 'text-blue-500' : 'text-emerald-500'}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{d.label}</p>
                        <p className="text-[10px] text-gray-500">{d.kind === 'invoice' ? 'Facture' : 'Bon de chargement'} • {d.status || '—'}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {d.amount > 0 && <p className="text-xs text-gray-900">{fmtFcfaFull(d.amount)}</p>}
                      <p className="text-[10px] text-gray-400">{fmtDateFr(d.createdAt)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="notes" className="overflow-auto px-6 pb-6 pt-4">
            {actions.length === 0 ? (
              <EmptyTab icon={StickyNote} title="Aucune note enregistrée" hint='Cliquez sur "Ajouter une note" pour enregistrer un commentaire.' />
            ) : (
              <ul className="space-y-3">
                {actions.map((a, i) => (
                  <li key={a.id || i} className="border border-gray-100 rounded-md p-3 hover:bg-gray-50" data-testid={`detail-action-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] capitalize">
                          {a.action}
                        </Badge>
                        {a.channel && <span className="text-[10px] text-gray-400">via {a.channel}</span>}
                        {a.visibility && a.visibility !== 'public' && (
                          <span className="text-[10px] text-gray-400">• {a.visibility}</span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400">{fmtDateFr(a.createdAt)}</span>
                    </div>
                    {a.subject && <p className="text-sm font-medium text-gray-900">{a.subject}</p>}
                    {a.body && <p className="text-xs text-gray-600 whitespace-pre-wrap mt-1">{a.body}</p>}
                    {a.result && <p className="text-[10px] text-emerald-600 mt-1">Résultat : {a.result}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">par {a.actorName || a.actorEmail || '—'}</p>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm" onClick={() => switchModal('relance')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="view-cta-relance"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Relancer
            </Button>
            <Button size="sm" variant="outline" onClick={() => switchModal('task')} data-testid="view-cta-task">
              <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Créer une tâche
            </Button>
            <Button size="sm" variant="outline" onClick={() => switchModal('note')} data-testid="view-cta-note">
              <StickyNote className="w-3.5 h-3.5 mr-1.5" /> Ajouter une note
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={close} data-testid="view-cta-close">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium text-right">{value}</dd>
    </div>
  );
}

function ContactBlock({ role, name, email, phone }) {
  return (
    <div className="border-l-2 border-emerald-200 pl-3">
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{role}</p>
      <p className="text-sm font-medium text-gray-900">{name}</p>
      <p className="text-gray-600">{email}</p>
      <p className="text-gray-600">{phone}</p>
    </div>
  );
}

function EmptyTab({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 text-gray-400">
      <Icon className="w-10 h-10 mb-2 opacity-50" />
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="text-xs mt-1 max-w-sm">{hint}</p>
    </div>
  );
}
